import { Component, useCallback, useEffect, useRef, useState } from "react"
import { Snackbar } from "@mui/material"
import { MdCheckCircle, MdPayments, MdClose } from "react-icons/md"
import ServicoAPI from "../../../servicos/ServicoAPI"
import "./campanhas-toast.css"

/**
 * Se o toast quebrar no render, some em silêncio e não derruba o mapa.
 */
class CampanhasToastBoundary extends Component {
	constructor(props) {
		super(props)
		this.state = { hasError: false }
	}

	static getDerivedStateFromError() {
		return { hasError: true }
	}

	componentDidCatch(error, errorInfo) {
		console.error("[CampanhasToast] erro isolado (mapa segue):", error, errorInfo)
	}

	render() {
		if (this.state.hasError) return null
		return this.props.children
	}
}

function dataIsoOffset(dias = 0) {
	const d = new Date()
	d.setDate(d.getDate() + dias)
	const y = d.getFullYear()
	const m = String(d.getMonth() + 1).padStart(2, "0")
	const day = String(d.getDate()).padStart(2, "0")
	return `${y}-${m}-${day}`
}

function jaAvisou(chave) {
	try {
		return !!localStorage.getItem(chave)
	} catch {
		return true
	}
}

function marcarAvisado(chave) {
	try {
		localStorage.setItem(chave, String(Date.now()))
	} catch {
		/* ignore */
	}
}

function pagamentoRecente(pagoEm, dias = 3) {
	const m = String(pagoEm || "").match(/^(\d{4}-\d{2}-\d{2})/)
	if (!m) return true
	return m[1] >= dataIsoOffset(-dias)
}

/**
 * Toasts compactos no mapa: meta atingida / crédito de campanha.
 */
function CampanhasNotificacoesMapa({ dados }) {
	const [snack, setSnack] = useState({
		open: false,
		tipo: "meta",
		titulo: "",
		nome: "",
		valor: "",
		detalhe: "",
	})
	const filaRef = useRef([])
	const mostrandoRef = useRef(false)
	const jaBuscouRef = useRef("")

	const mostrarProximo = useCallback(() => {
		if (mostrandoRef.current) return
		const proximo = filaRef.current.shift()
		if (!proximo) return
		mostrandoRef.current = true
		setSnack({
			open: true,
			tipo: proximo.tipo,
			titulo: proximo.titulo,
			nome: proximo.nome,
			valor: proximo.valor,
			detalhe: proximo.detalhe || "",
		})
	}, [])

	const enfileirar = useCallback((itens) => {
		if (!itens.length) return
		filaRef.current.push(...itens)
		mostrarProximo()
	}, [mostrarProximo])

	const fecharSnack = useCallback((_, reason) => {
		if (reason === "clickaway") return
		setSnack((prev) => ({ ...prev, open: false }))
		mostrandoRef.current = false
		window.setTimeout(() => {
			mostrarProximo()
		}, 220)
	}, [mostrarProximo])

	useEffect(() => {
		const idProf = dados?.idProf
		const token = dados?.token
		const dominio = dados?.dominio
		if (!idProf || !token || !dominio) return

		const assinatura = `${dominio}|${idProf}|${token}`
		if (jaBuscouRef.current === assinatura) return
		jaBuscouRef.current = assinatura

		let cancelado = false

		;(async () => {
			const avisos = []

			try {
				const resAtivas = await ServicoAPI.campanhasBonificacaoApp(dados)
				if (cancelado) return
				const lista = Array.isArray(resAtivas?.dados?.lista) ? resAtivas.dados.lista : []

				for (const item of lista) {
					const bateuMeta =
						item?.status === "concluida"
						|| (Number(item?.percentual) >= 100 && item?.status !== "pago")

					if (!bateuMeta) continue

					const chave = `camp_toast_meta_${idProf}_${item.id}_${item.cicloInicio || ""}_${item.cicloFim || ""}`
					if (jaAvisou(chave)) continue
					marcarAvisado(chave)

					avisos.push({
						tipo: "meta",
						titulo: "Meta atingida",
						nome: item.nome || "Meta",
						valor: item.valorBonusFormatado || "",
						detalhe: item.previsaoPagamentoTexto
							? `Previsto para ${item.previsaoPagamentoTexto}`
							: "Aguardando fechamento do ciclo",
					})
				}
			} catch {
				/* silencioso */
			}

			try {
				const dataFim = dataIsoOffset(0)
				const dataInicio = dataIsoOffset(-14)
				const resPag = await ServicoAPI.campanhasBonificacaoPagamentosApp(dados, {
					dataInicio,
					dataFim,
				})
				if (cancelado) return
				const listaPag = Array.isArray(resPag?.dados?.lista) ? resPag.dados.lista : []

				for (const item of listaPag) {
					const idPag = item?.id
					if (!idPag) continue
					if (!pagamentoRecente(item.pagoEm, 3)) continue
					const chave = `camp_toast_credito_${idProf}_${idPag}`
					if (jaAvisou(chave)) continue
					marcarAvisado(chave)

					avisos.push({
						tipo: "credito",
						titulo: "Crédito inserido",
						nome: item.nome || "Meta",
						valor: item.valorBonusFormatado || "",
						detalhe: item.pagoEmTexto ? `Pago em ${item.pagoEmTexto}` : "Bônus creditado na conta",
					})
				}
			} catch {
				/* silencioso */
			}

			if (!cancelado && avisos.length) {
				enfileirar(avisos)
			}
		})()

		return () => {
			cancelado = true
		}
	}, [dados?.idProf, dados?.token, dados?.dominio, dados, enfileirar])

	const Icone = snack.tipo === "credito" ? MdPayments : MdCheckCircle

	return (
		<Snackbar
			open={snack.open}
			autoHideDuration={5200}
			onClose={fecharSnack}
			anchorOrigin={{ vertical: "top", horizontal: "center" }}
			sx={{
				"&.MuiSnackbar-root": {
					top: "max(0.75rem, env(safe-area-inset-top, 0px)) !important",
					left: "50% !important",
					right: "auto !important",
					transform: "translateX(-50%)",
					width: "auto",
					maxWidth: "min(94vw, 22rem)",
				},
			}}
		>
			<div
				className={`camp-toast camp-toast--${snack.tipo}`}
				role="status"
				aria-live="polite"
			>
				<span className="camp-toast__icone" aria-hidden>
					<Icone size={20} />
				</span>
				<div className="camp-toast__corpo">
					<div className="camp-toast__linha">
						<span className="camp-toast__titulo">{snack.titulo}</span>
						{snack.valor ? (
							<strong className="camp-toast__valor">{snack.valor}</strong>
						) : null}
					</div>
					<p className="camp-toast__nome">{snack.nome}</p>
					{snack.detalhe ? (
						<p className="camp-toast__detalhe">{snack.detalhe}</p>
					) : null}
				</div>
				<button
					type="button"
					className="camp-toast__fechar"
					aria-label="Fechar"
					onClick={() => fecharSnack()}
				>
					<MdClose size={18} aria-hidden />
				</button>
			</div>
		</Snackbar>
	)
}

export default function CampanhasNotificacoesMapaSeguro(props) {
	return (
		<CampanhasToastBoundary>
			<CampanhasNotificacoesMapa {...props} />
		</CampanhasToastBoundary>
	)
}
